/**
 * compareLeads — pure lead-list rules for the Compare page.
 *
 * Kept out of Compare.tsx (which is a large component with a React-only
 * `useMemo`) so the data-integrity invariant below can be unit-tested without a
 * renderer, exactly as tradeMatchModel.ts does for the Trade Match surface.
 *
 * THE INVARIANT (reported 2026-08-07)
 * -----------------------------------
 * The two Compare panels — "You have, they don't" and "They have, you don't" —
 * are TRADE leads: every tile carries an add-to-trade-list action. /compare
 * built them from pure dex set-difference, so species Pokémon GO refuses to
 * trade surfaced as leads nobody can act on. With the Lucky filter on, a
 * friend's Mythicals (Mew, Celebi, Deoxys, Darkrai, Shaymin, Keldeo, Meloetta,
 * Genesect, Diancie) all listed under "They have, you don't" — a Mythical can
 * be neither traded nor made Lucky, since Lucky comes only from a trade.
 *
 * The gate is the Game Master's tradability roster, NOT Mythical-ness: that
 * proxy is wrong in both directions (Zygarde #718 and Eternatus #890 are
 * untradable Legendaries; Meltan #808 and Melmetal #809 are tradable
 * Mythicals). The worker applies the same gate server-side; this is the
 * on-device half, so an app running against an older deploy still can't show a
 * dead lead.
 */
import { isTradableSpecies } from "./variantAvailability";

/** The shape /compare returns per matched variant (the fields leads care about). */
export interface CompareLeadRow {
  speciesId: number;
}

/**
 * Drop the untradable rows and restate the panel total.
 *
 * `serverTotal` is the endpoint's true count: for Pro `rows` is the whole set,
 * for Basic it is a truncated preview whose real length lives in the *Total
 * field. Subtracting what we dropped is therefore exact for Pro, and for Basic
 * can only move the total toward the truth (never above it) — a Basic tease of
 * "+N with Pro" that is slightly high beats one that counts dead leads.
 */
export function tradableLeads<T extends CompareLeadRow>(
  rows: readonly T[],
  serverTotal: number,
): { rows: T[]; total: number } {
  const kept = rows.filter((r) => isTradableSpecies(r.speciesId));
  return { rows: kept, total: Math.max(0, serverTotal - (rows.length - kept.length)) };
}
